import React, { useState, useCallback } from 'react';
import { 
  CogIcon,
  XMarkIcon,
  UserIcon,
  BellIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
  EyeIcon,
  ClockIcon,
  ComputerDesktopIcon,
  CheckIcon,
  ChevronRightIcon,
  ChartBarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserSettings {
  profile: {
    name: string;
    email: string;
    avatar?: string;
    timezone: string;
    language: string;
  };
  preferences: {
    autoSave: boolean;
    notifications: {
      email: boolean;
      push: boolean;
      desktop: boolean;
      sound: boolean;
    };
    privacy: {
      analytics: boolean;
      errorReporting: boolean;
      usageData: boolean;
    };
    appearance: {
      theme: 'light' | 'dark' | 'system';
      fontSize: 'small' | 'medium' | 'large';
      sidebarCollapsed: boolean;
      compactMode: boolean;
    };
    analysis: {
      autoAnalyze: boolean;
      deepScan: boolean;
      includeTests: boolean;
      ignoreNodeModules: boolean;
      maxFileSize: number;
    };
    exports: {
      defaultFormat: 'pdf' | 'json' | 'csv';
      includeCharts: boolean;
      includeRecommendations: boolean;
      compression: boolean;
    };
  };
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose }) => {
  const { theme, setTheme } = useTheme();
  const { success: showSuccess } = useNotifications();
  const [activeTab, setActiveTab] = useState('profile');
  const [hasChanges, setHasChanges] = useState(false);

  const [settings, setSettings] = useState<UserSettings>({
    profile: {
      name: 'John Developer',
      email: 'john@example.com',
      timezone: 'America/New_York',
      language: 'en'
    },
    preferences: {
      autoSave: true,
      notifications: {
        email: true,
        push: true,
        desktop: false,
        sound: false
      },
      privacy: {
        analytics: true,
        errorReporting: true,
        usageData: false
      },
      appearance: {
        theme: theme as 'light' | 'dark' | 'system',
        fontSize: 'medium',
        sidebarCollapsed: false,
        compactMode: false
      },
      analysis: {
        autoAnalyze: true,
        deepScan: false,
        includeTests: true,
        ignoreNodeModules: true,
        maxFileSize: 50
      },
      exports: {
        defaultFormat: 'pdf',
        includeCharts: true,
        includeRecommendations: true,
        compression: false
      }
    }
  });

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserIcon },
    { id: 'appearance', name: 'Appearance', icon: PaintBrushIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'analysis', name: 'Analysis', icon: ChartBarIcon },
    { id: 'exports', name: 'Exports', icon: DocumentTextIcon },
    { id: 'privacy', name: 'Privacy', icon: ShieldCheckIcon },
    { id: 'advanced', name: 'Advanced', icon: CogIcon }
  ];

  const updateSetting = useCallback((path: string, value: any) => {
    setSettings(prev => {
      const keys = path.split('.');
      const updated = { ...prev };
      let current: any = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setHasChanges(true);
  }, []);

  const handleThemeChange = useCallback((newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    updateSetting('preferences.appearance.theme', newTheme);
  }, [setTheme, updateSetting]);

  const saveSettings = useCallback(() => {
    // Here you would typically save to localStorage or send to backend
    localStorage.setItem('universalCrownPrimeSettings', JSON.stringify(settings));
    setHasChanges(false);
    showSuccess('Settings Saved', 'Your preferences have been updated successfully');
  }, [settings, showSuccess]);

  const resetToDefaults = useCallback(() => {
    setSettings({
      profile: {
        name: 'John Developer',
        email: 'john@example.com',
        timezone: 'America/New_York',
        language: 'en'
      },
      preferences: {
        autoSave: true,
        notifications: {
          email: true,
          push: true,
          desktop: false,
          sound: false
        },
        privacy: {
          analytics: true,
          errorReporting: true,
          usageData: false
        },
        appearance: {
          theme: 'system',
          fontSize: 'medium',
          sidebarCollapsed: false,
          compactMode: false
        },
        analysis: {
          autoAnalyze: true,
          deepScan: false,
          includeTests: true,
          ignoreNodeModules: true,
          maxFileSize: 50
        },
        exports: {
          defaultFormat: 'pdf',
          includeCharts: true,
          includeRecommendations: true,
          compression: false
        }
      }
    });
    setHasChanges(true);
  }, []);

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div>
        <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Full Name
        </label>
        <input
          id="profile-name"
          type="text"
          value={settings.profile.name}
          onChange={(e) => updateSetting('profile.name', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          placeholder="Enter your full name"
        />
      </div>
      <div>
        <label htmlFor="profile-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Email Address
        </label>
        <input
          id="profile-email"
          type="email"
          value={settings.profile.email}
          onChange={(e) => updateSetting('profile.email', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          placeholder="name@example.com"
        />
      </div>
      <div>
        <label htmlFor="profile-timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Timezone
        </label>
        <select
          id="profile-timezone"
          value={settings.profile.timezone}
          onChange={(e) => updateSetting('profile.timezone', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="America/New_York">Eastern Time</option>
          <option value="America/Chicago">Central Time</option>
          <option value="America/Denver">Mountain Time</option>
          <option value="America/Los_Angeles">Pacific Time</option>
          <option value="Europe/London">London</option>
          <option value="Europe/Paris">Paris</option>
          <option value="Asia/Tokyo">Tokyo</option>
        </select>
      </div>
    </div>
  );

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      <div>
        <label id="appearance-theme-label" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Theme
        </label>
        <div className="grid grid-cols-3 gap-3" role="radiogroup" aria-labelledby="appearance-theme-label">
          {(['light', 'dark', 'system'] as const).map((themeOption) => (
            <button
              key={themeOption}
              onClick={() => handleThemeChange(themeOption)}
              className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                settings.preferences.appearance.theme === themeOption
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
              }`}
              role="radio"
              aria-checked={settings.preferences.appearance.theme === themeOption}
            >
              <div className="flex items-center space-x-2">
                {themeOption === 'light' && <EyeIcon className="h-5 w-5" />}
                {themeOption === 'dark' && <ClockIcon className="h-5 w-5" />}
                {themeOption === 'system' && <ComputerDesktopIcon className="h-5 w-5" />}
                <span className="text-sm font-medium capitalize">{themeOption}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      
      <div>
        <label htmlFor="fontSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Font Size
        </label>
        <select
          id="fontSize"
          value={settings.preferences.appearance.fontSize}
          onChange={(e) => updateSetting('preferences.appearance.fontSize', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          aria-label="Font size selection"
        >
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
        </select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Compact Mode
          </label>
            <label className="relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer">
              <input
                type="checkbox"
                className="sr-only"
                checked={settings.preferences.appearance.compactMode}
                onChange={(e) => updateSetting('preferences.appearance.compactMode', e.target.checked)}
                aria-label="Toggle compact mode"
              />
              <span className={`absolute inset-0 rounded-full transition-colors ${
                settings.preferences.appearance.compactMode ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`} />
              <span
                className={`relative inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.preferences.appearance.compactMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </label>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        {Object.entries(settings.preferences.notifications).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                {key.replace(/([A-Z])/g, ' $1')} Notifications
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {key === 'email' && 'Receive notifications via email'}
                {key === 'push' && 'Browser push notifications'}
                {key === 'desktop' && 'Desktop system notifications'}
                {key === 'sound' && 'Play sound for notifications'}
              </p>
            </div>
              <label className="relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={value}
                  onChange={(e) => updateSetting(`preferences.notifications.${key}`, e.target.checked)}
                  aria-label={`Toggle ${key} notifications`}
                />
                <span className={`absolute inset-0 rounded-full transition-colors ${
                  value ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                }`} />
                <span
                  className={`relative inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </label>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnalysisTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Auto-Analyze Projects
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Automatically analyze projects when opened
            </p>
          </div>
            <label className="relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer">
              <input
                type="checkbox"
                className="sr-only"
                checked={settings.preferences.analysis.autoAnalyze}
                onChange={(e) => updateSetting('preferences.analysis.autoAnalyze', e.target.checked)}
                aria-label="Toggle auto-analyze projects"
              />
              <span className={`absolute inset-0 rounded-full transition-colors ${
                settings.preferences.analysis.autoAnalyze ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`} />
              <span
                className={`relative inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.preferences.analysis.autoAnalyze ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Deep Scan
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Perform thorough analysis (slower but more accurate)
            </p>
          </div>
            <label className="relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer">
              <input
                type="checkbox"
                className="sr-only"
                checked={settings.preferences.analysis.deepScan}
                onChange={(e) => updateSetting('preferences.analysis.deepScan', e.target.checked)}
                aria-label="Toggle deep scan"
              />
              <span className={`absolute inset-0 rounded-full transition-colors ${
                settings.preferences.analysis.deepScan ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`} />
              <span
                className={`relative inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.preferences.analysis.deepScan ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Include Test Files
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Include test files in analysis
            </p>
          </div>
            <label className="relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer">
              <input
                type="checkbox"
                className="sr-only"
                checked={settings.preferences.analysis.includeTests}
                onChange={(e) => updateSetting('preferences.analysis.includeTests', e.target.checked)}
                aria-label="Toggle include test files"
              />
              <span className={`absolute inset-0 rounded-full transition-colors ${
                settings.preferences.analysis.includeTests ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`} />
              <span
                className={`relative inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.preferences.analysis.includeTests ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </label>
        </div>
      </div>

      <div>
        <label htmlFor="maxFileSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Max File Size (MB)
        </label>
        <input
          id="maxFileSize"
          type="range"
          min="1"
          max="100"
          value={settings.preferences.analysis.maxFileSize}
          onChange={(e) => updateSetting('preferences.analysis.maxFileSize', parseInt(e.target.value))}
          className="w-full"
          aria-label="Maximum file size slider"
        />
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>1 MB</span>
          <span>{settings.preferences.analysis.maxFileSize} MB</span>
          <span>100 MB</span>
        </div>
      </div>
    </div>
  );

  const renderExportsTab = () => (
    <div className="space-y-6">
      <div>
        <label htmlFor="exportFormat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Default Export Format
        </label>
        <select
          id="exportFormat"
          value={settings.preferences.exports.defaultFormat}
          onChange={(e) => updateSetting('preferences.exports.defaultFormat', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-2-blue-500"
          aria-label="Default export format selection"
        >
          <option value="pdf">PDF Report</option>
          <option value="json">JSON Data</option>
          <option value="csv">CSV Format</option>
        </select>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Include Charts
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Include visualization charts in exports
            </p>
          </div>
            <label className="relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer">
              <input
                type="checkbox"
                className="sr-only"
                checked={settings.preferences.exports.includeCharts}
                onChange={(e) => updateSetting('preferences.exports.includeCharts', e.target.checked)}
                aria-label="Toggle include charts in exports"
              />
              <span className={`absolute inset-0 rounded-full transition-colors ${
                settings.preferences.exports.includeCharts ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`} />
              <span
                className={`relative inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.preferences.exports.includeCharts ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Include Recommendations
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Include improvement recommendations
            </p>
          </div>
            <label className="relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer">
              <input
                type="checkbox"
                className="sr-only"
                checked={settings.preferences.exports.includeRecommendations}
                onChange={(e) => updateSetting('preferences.exports.includeRecommendations', e.target.checked)}
                aria-label="Toggle include recommendations in exports"
              />
              <span className={`absolute inset-0 rounded-full transition-colors ${
                settings.preferences.exports.includeRecommendations ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
              }`} />
              <span
                className={`relative inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.preferences.exports.includeRecommendations ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </label>
        </div>
      </div>
    </div>
  );

  const renderPrivacyTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        {Object.entries(settings.preferences.privacy).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                {key.replace(/([A-Z])/g, ' $1')}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {key === 'analytics' && 'Help improve the app with anonymous usage analytics'}
                {key === 'errorReporting' && 'Automatically report errors to help fix bugs'}
                {key === 'usageData' && 'Share usage data for feature improvements'}
              </p>
            </div>
              <label className="relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={value}
                  onChange={(e) => updateSetting(`preferences.privacy.${key}`, e.target.checked)}
                  aria-label={`Toggle ${key}`}
                />
                <span className={`absolute inset-0 rounded-full transition-colors ${
                  value ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                }`} />
                <span
                  className={`relative inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </label>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAdvancedTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Auto-Save
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Automatically save settings and preferences
          </p>
        </div>
          <label className="relative inline-flex h-6 w-11 items-center rounded-full cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              checked={settings.preferences.autoSave}
              onChange={(e) => updateSetting('preferences.autoSave', e.target.checked)}
              aria-label="Toggle auto-save"
            />
            <span className={`absolute inset-0 rounded-full transition-colors ${
              settings.preferences.autoSave ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
            }`} />
            <span
              className={`relative inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.preferences.autoSave ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </label>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Danger Zone</h3>
        <div className="space-y-3">
          <button
            onClick={resetToDefaults}
            className="w-full px-4 py-2 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/50 transition-colors"
          >
            Reset to Defaults
          </button>
          <button className="w-full px-4 py-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors">
            Clear All Data
          </button>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile': return renderProfileTab();
      case 'appearance': return renderAppearanceTab();
      case 'notifications': return renderNotificationsTab();
      case 'analysis': return renderAnalysisTab();
      case 'exports': return renderExportsTab();
      case 'privacy': return renderPrivacyTab();
      case 'advanced': return renderAdvancedTab();
      default: return renderProfileTab();
    }
  };

  return (
      isOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fade-in"
          onClick={(e) => e.target === e.currentTarget && onClose()}
          role="presentation"
          aria-hidden="true"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-fade-in-up"
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-title"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 id="settings-title" className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <CogIcon className="h-6 w-6 mr-2 text-blue-600" />
                Settings
              </h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="Close settings"
                aria-label="Close settings"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="flex h-[calc(90vh-200px)]">
              {/* Sidebar */}
              <div className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
                <nav className="p-4 space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors ${
                          activeTab === tab.id
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Icon className="h-5 w-5 mr-3" />
                        <span className="font-medium">{tab.name}</span>
                        <ChevronRightIcon className="h-4 w-4 ml-auto" />
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-6 animate-fade-in">
                  {renderTabContent()}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center space-x-2">
                {hasChanges && (
                  <span className="text-sm text-yellow-600 dark:text-yellow-400">
                    You have unsaved changes
                  </span>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveSettings}
                  disabled={!hasChanges}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                    hasChanges
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null
  );
};

export default SettingsPanel;
